import * as React from "react";

import { cn } from "@facade/lib/utils";

type FloorbitLogoSize = "sm" | "md" | "lg";

const sizeClasses: Record<
  FloorbitLogoSize,
  { container: string; icon: string; label: string }
> = {
  sm: {
    container: "gap-1.5",
    icon: "size-6",
    label: "text-lg",
  },
  md: {
    container: "gap-2",
    icon: "size-10",
    label: "text-lg",
  },
  lg: {
    container: "gap-3",
    icon: "size-16",
    label: "text-2xl",
  },
};

interface FloorbitLogoProps extends React.ComponentProps<"div"> {
  size?: FloorbitLogoSize;
  animated?: boolean;
}

const tilePositions = [0, 24, 48, 72] as const;
const tileOffsets = [-3, -1, 1, 3] as const;

function FloorbitLogo({
  size = "md",
  animated = false,
  children,
  className,
  ...props
}: FloorbitLogoProps) {
  const classes = sizeClasses[size];

  return (
    <div
      className={cn(
        "inline-flex items-center font-heading font-semibold",
        classes.container,
        className,
      )}
      {...props}
    >
      <svg
        aria-hidden="true"
        data-animated={animated || undefined}
        className={cn("shrink-0", animated && "overflow-visible", classes.icon)}
        viewBox="0 0 92 92"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g className={cn(animated && "floorbit-logo-rotor")}>
          {tilePositions.flatMap((y, row) =>
            tilePositions.map((x, column) => {
              const accent =
                (row === 1 || row === 2) && (column === 1 || column === 2);
              const style = animated
                ? ({
                    "--floorbit-logo-x": `${tileOffsets[column]}px`,
                    "--floorbit-logo-y": `${tileOffsets[row]}px`,
                  } as React.CSSProperties)
                : undefined;

              return (
                <g
                  key={`${row}-${column}`}
                  className={cn(animated && "floorbit-logo-tile")}
                  style={style}
                >
                  <rect
                    x={x}
                    y={y}
                    width="20"
                    height="20"
                    rx="3"
                    className={
                      accent ? "fill-brand-accent" : "fill-brand-primary"
                    }
                  />
                </g>
              );
            }),
          )}
        </g>
      </svg>
      {children}
    </div>
  );
}

export { FloorbitLogo };
export type { FloorbitLogoProps, FloorbitLogoSize };
